import { TransportType } from './transport-type';
import { EquipmentType } from './equipment-type';
import { EquipmentDetail } from './equipment-detail';
import { EngineCategory } from './engine-category';
import { EngineType } from './engine-type';
import { OpcoCode } from './opco';
import { TourActionPointType } from './tour.interface';
import { Address } from './address.interface';
import { TransportNoteForDriver } from './note-for-driver.interface';

export interface Transport {
  readonly id: string;
  readonly opcoCode: OpcoCode;
  readonly earliest: string;
  readonly latest: string;
  readonly name: string;
  readonly orderReference: string | null;
  readonly purpose: TransportPurpose;
  readonly type: TransportType;
  readonly providerType: TransportProviderType;
  readonly c2c: boolean;
  readonly destination: TransportDestination;
  readonly contactOnSite: Contact | null;
  readonly contract: Contract | null;
  readonly instance: Instance | null;
  readonly cargoDescription: string | null;
  readonly notes: string | null;
  readonly notesForDriver: TransportNoteForDriver[];
  readonly options: TransportOptions[];
  readonly documents: Document[];
  readonly searchValues: string;
  readonly allowedActions: AllowedActions;
  readonly completed: boolean;
}

export type TransportProviderType = 'OPCO' | 'SUBRENT_SUPPLIER' | 'CUSTOMER' | 'TRANSPORT_SUPPLIER' | 'OPERATOR';

export interface TransportDestination {
  readonly depotCode: string | null;
  readonly address: Address | null;
}

export enum SelfTransportDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING'
}

interface Contact {
  readonly name: string;
  readonly phone?: string;
  readonly mobile?: string;
  readonly email?: string;
}

export interface Document {
  readonly id: string;
  readonly name: string;
}

interface Contract {
  readonly customerName: string;
  readonly administrativeContact: Contact;
  readonly responsibleDepot: { name: string; phone: string | null } | null;
  readonly responsibleEmail: string | null;
  readonly requirements: string[];
}

export interface Instance {
  readonly id: string;
  readonly objectIdAndDisplayName: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly modelGroupName: string;
  readonly equipmentType: EquipmentType;
  readonly equipmentDetailCategory: EquipmentDetail;
  readonly engineCategory: EngineCategory;
  readonly engineType: EngineType | null;
  readonly location: InstanceLocation;
  readonly blocked: boolean;
  readonly blockedReason?: string;
  readonly technicalDetails: TechnicalDetails;
  readonly vendor?: Vendor;
  readonly icon: string;
  readonly qlocator: string;
}

export interface Vendor {
  name: string;
  vendorGroupCode: string;
}

interface InstanceLocation {
  readonly rasterLocation: string | null;
  readonly type: InstanceLocationType | null;
  readonly lastTransportAction: {
    readonly userName: string;
    readonly timestamp: string;
  } | null;
}

export enum InstanceLocationType {
  RASTER = 'Raster',
  ADDRESS = 'Address',
  OUTSIDE = 'Outside',
  IN_TRANSPORT = 'InTransport',
  SUPPLIER = 'Supplier'
}

interface TechnicalDetails {
  readonly minimumTransportHeight?: Measurement;
  readonly minimumTransportWidth?: Measurement;
  readonly minimumTransportLength?: Measurement;
  readonly grossVehicleWeight?: Measurement;
}

interface Measurement {
  value: number;
  unit: string;
}

export interface AllowedActions {
  readonly load: boolean;
  readonly loadDisabledReasons: string[];
  readonly unloadWithoutRasterLocation: boolean;
  readonly unloadWithRasterLocation: boolean;
  readonly unloadDisabledReasons: string[];
  readonly returnCargo: boolean;
  readonly returnCargoDisabledReasons: string[];
  readonly startProtocolForLoad: boolean;
  readonly startProtocolForUnload: boolean;
  readonly viewProtocolForLoad: boolean;
  readonly viewProtocolForUnload: boolean;
  readonly refuel: boolean;
  readonly switchMachine: boolean;
  readonly messageForCounterparty: boolean;
}

export function isRasterLocationRequired(
  tourActionPointType: TourActionPointType,
  { unloadWithRasterLocation, returnCargo }: AllowedActions
) {
  switch (tourActionPointType) {
    case TourActionPointType.LOAD:
      return false;
    case TourActionPointType.UNLOAD:
      return unloadWithRasterLocation;
    case TourActionPointType.RETURN_CARGO:
      return returnCargo;
  }
}

export enum TransportOptions {
  PRE_REGISTRATION = 'PRE_REGISTRATION',
  RAMP_DELIVERY = 'RAMP_DELIVERY',
  EXCEPTIONAL_TRANSPORTATION = 'EXCEPTIONAL_TRANSPORTATION',
  SMALL_TRUCK = 'SMALL_TRUCK',
  CLOSED_TRUCK = 'CLOSED_TRUCK',
  LOW_DECK = 'LOW_DECK'
}

export enum TransportPurpose {
  RENTAL = 'RENTAL',
  SUBRENTAL = 'SUBRENTAL',
  STOCK_ROTATION = 'STOCK_ROTATION',
  CUSTOMER = 'CUSTOMER',
  SERVICE = 'SERVICE',
  SALES = 'SALES',
  PURCHASE = 'PURCHASE',
  FINANCIAL_LEASE = 'FINANCIAL_LEASE',
  OPERATIONAL_LEASE = 'OPERATIONAL_LEASE'
}
